import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { OrderItem } from 'contracts/OrderItems';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import { Edit } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import CancelItemModal, { Ref as CancelItemModalRef } from './CancelItemModal';
import EditItemBatchModal, {
  Ref as EditItemBatchModalRef,
} from './EditItemBatchModal';

import * as S from './styles';

interface Props {
  items: OrderItem[];
  onUpdate?: () => void;
}

const OrderItems: React.FC<Props> = ({ items, onUpdate }) => {
  const [itemsState, setItemsState] = useState<OrderItem[]>(items);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const editItemBatchModalRef = useRef<EditItemBatchModalRef>(null);
  const cancelItemModalRef = useRef<CancelItemModalRef>(null);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);

  useEffect(() => {
    setItemsState(items);
  }, [items]);

  const { cancelOrderItems } = useOrder();

  const { mutate: itemMutation, isPending: isUpdating } = useMutation({
    mutationFn: (items: any) => cancelOrderItems(items),
    onSuccess: (data: any) => {
      toast('Itens cancelados com sucesso');
      onUpdate?.();
    },
    onError: (error: any) => {
      toast('Erro ao editar item', {
        description: error.response.data.message,
      });
    },
  });

  const handleModalOpen = (item: OrderItem) => {
    setSelectedItem(item);
    editItemBatchModalRef.current?.openModal();
  };

  // const handleBatchUpdate = (items: any) => {
  //   setItemsState(items.data);
  // };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItems((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
      return newSelected;
    });
  };

  const handleModalCancelOpen = (ids: number[]) => {
    //deve sempre existir pelo menos um item no pedido
    if (itemsState.length === selectedItems.size) {
      toast('O pedido deve ficar com pelo menos um item.');
      return;
    }

    cancelItemModalRef.current?.openModal();
  };

  return (
    <>
      <EditItemBatchModal
        item={selectedItem || ({} as OrderItem)}
        ref={editItemBatchModalRef}
        onUpdate={onUpdate}
      />

      <CancelItemModal
        ids={Array.from(selectedItems) || []}
        ref={cancelItemModalRef}
        onUpdate={onUpdate}
      />

      <S.List>
        <S.ListHeader>
          <S.Column></S.Column>
          <S.Column>Código</S.Column>
          <S.Column>Descrição</S.Column>
          <S.Column>Quantidade</S.Column>
          <S.Column>Volume</S.Column>
          <S.Column>Unidade</S.Column>
          <S.Column>Status</S.Column>
          <S.Column>Lote</S.Column>
        </S.ListHeader>
        {itemsState.map((item) => (
          <S.ListItem key={item.id}>
            <S.Column>
              <input
                type="checkbox"
                checked={selectedItems.has(item.id)}
                onChange={() => toggleItemSelection(item.id)}
              />
            </S.Column>
            <S.Column>{item.code}</S.Column>
            <S.Column>{item.description}</S.Column>
            <S.Column>
              {item.quantity.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}
            </S.Column>
            <S.Column>
              {item.volume.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}
            </S.Column>
            <S.Column>{item.unit}</S.Column>
            <S.Column>
              {item.deletedAt ? 'Cancelado' : 'Ativo'}
            </S.Column>
            <S.Column>
              <div className="flex gap-2 items-center justify-between">
                <span>{item.batch || '---'}</span>
                <Button disabled={item.deletedAt !== null} className="h-8" onClick={() => handleModalOpen(item)}>
                  <Edit width={16} height={16} />
                </Button>
              </div>
            </S.Column>
          </S.ListItem>
        ))}
      </S.List>
      <div>
        <Button
          onClick={() => handleModalCancelOpen(Array.from(selectedItems))}
          size="sm"
          variant="destructive"
          disabled={selectedItems.size === 0 || isUpdating}
        >
          Cancelar items
        </Button>
      </div>
    </>
  );
};

export default OrderItems;
